import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Button from "../../../../../../components/base/button/button";
import {Box, Card, Checkbox, Chip, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography, Unstable_Grid2 as Grid, useTheme} from "@mui/material";
import ProductVarianceImportDialog from "./import-dialog";
import {ReactComponent as Excel} from '../../../../../../../assets/images/excel.svg';
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import {MarkProductForCacheSectionNames, ProductTechnicalPropertyTypeIds} from "../../../../../../../core/constants/enums";
import {Tree, TreeNode} from "react-organizational-chart";
import LookupProductCard from "../../../../../../components/app-specific/lookup-product-card";
import Utils from "../../../../../../../core/services/utils/utils";
import axios from "axios";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import Api from "../../../../../../../core/services/api/api";
import {useDispatch} from "react-redux";
import ReduxStoreRoutes from "../../../../../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../../../../../redux/slices/dialogs/actions";
import {ArrowRightAltOutlined, ExpandLessRounded, ExpandMoreRounded, SearchOutlined, TextFormatRounded, UnfoldLessRounded, UnfoldMoreRounded, WidthFullRounded} from "@mui/icons-material";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import {DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import useSavedComponentsState from "../../../../../../hooks/use-saved-components-state";
import {useParams} from "react-router-dom";
import ApiResponseCodes from "../../../../../../../core/models/static/api-response-codes";
import PartAutocomplete from "../../../../../../components/text-fields/autocomplete/part-autocomplete";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../../components/base/chips/chips";


const NULL_VALUE_PLACEHOLDER = "--";

const createHierarchyObject = (products, properties) => {
    const hierarchy = {};
    for (const product of products) {
        let level = hierarchy; // mutable current level

        for (const property of properties) {
            const {raw, formatted} = Utils.resolveVariantPropertyDisplay(
                property.industryPropertyTypeId,
                product.properties,
                property.id,
                property.nullReplacement,
            );
            let value = property.industryPropertyTypeId === ProductTechnicalPropertyTypeIds.color
                ? raw
                : formatted;
            if (value === null || value === undefined || value === "") {
                value = NULL_VALUE_PLACEHOLDER;
            }
            if (!level[value])
                level[value] = {};
            level = level[value];
        }
        if (!level.__items)
            level.__items = [];
        level.__items.push(product);
    }
    return hierarchy;

};

const hierarchyObjectToArray = (hierarchyObj, properties, colors, levelIndex = 0, parentPath = "") => {
    const nodes = [];
    const property = properties[levelIndex]; // might be undefined at leaf
    const color = colors[(levelIndex + 1) % colors.length];

    for (const [value, child] of Object.entries(hierarchyObj)) {
        if (value === "__items") continue;
        const nodePath = parentPath ? `${parentPath} > ${value}` : value;
        const children = hierarchyObjectToArray(child, properties, colors, levelIndex + 1, nodePath);
        const products = child.__items ?? [];
        const showProducts = products.length > 0 && children.length === 0;
        nodes.push({
            id: nodePath, // stable enough for UI keys
            property: property,
            products: [],
            value: value,
            showProducts: false,
            children: children.length ? children : showProducts ? [
                {
                    id: `${nodePath}-products`,
                    property: null,
                    value: null,
                    products: products,
                    showProducts: true,
                    children: [],
                }
            ] : undefined,
            color: color,
            backgroundColor: `${color}2d`,
        });
    }

    return nodes;
};


const initialImportDialogState = {open: false, close: () => void 0, onImport: () => void 0}

const ProductVarianceSection = ({loading: loadingProps, product, setIsCacheNeeded}) => {
    const params = useParams();
    const [loadingVariants, setLoadingVariants] = useState(true);
    const [variants, setVariants] = useState([]);
    const [properties, setProperties] = useState([])
    const [availabilities, setAvailabilities] = useState({});
    const [fetchingProductAvailabilities, setFetchingProductAvailabilities] = useState(false);
    const [warehouseIds, setWarehouseIds] = useState([]);
    const [removingPartNumbers, setRemovingPartNumbers] = useState([]);
    const [importDialogState, setImportDialogState] = useState(initialImportDialogState);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();

    const operatorAccessLevels = useAccessLevels();
    const theme = useTheme();
    const loading = useMemo(() => loadingProps || loadingVariants, [loadingProps, loadingVariants])
    const partNumber = useMemo(() => product?.partNo ?? product?.partNumber, [product?.partNo, product?.partNumber])

    const [viewMode, setViewMode] = useState('partNo'); // 'card' or 'partNo'
    const [loadingViewMode, setLoadingViewMode] = useState(true);
    const {highestVersion: getCachedViewMode, save: saveCachedViewMode} = useSavedComponentsState({name: "product-info-variance-view-mode", version: '1.0.0'});

    /**
     * Fetches the cached view mode.
     * @type {(function(): Promise<void>)}
     */
    const getViewMode = useCallback(async () => {
        setLoadingViewMode(true);
        const cachedMode = await getCachedViewMode();
        if (cachedMode?.data) {
            setViewMode(cachedMode.data);
        }
        setLoadingViewMode(false);
    }, [getCachedViewMode]);

    /**
     * Loads the cached view mode on component mount.
     */
    useEffect(() => {
        void getViewMode();
    }, [getCachedViewMode, getViewMode]);

    /**
     * Toggles the view mode between grid and list.
     * @type {(function(): void)}
     */
    const toggleViewMode = useCallback(() => {
        setViewMode(prevMode => {
            const newMode = prevMode === 'card' ? 'partNo' : 'card';
            void saveCachedViewMode(newMode);
            return newMode;
        });
    }, [saveCachedViewMode]);

    /**
     * Fetches both the technical properties of the current product and its filled values.
     *
     * * merges them into a single list before storing it in the state.
     * @type {(function(): Promise<void>)}
     */
    const getProductVariants = useCallback(async () => {
        if (!partNumber)
            return;
        if (!operatorAccessLevels.packages.lookup.productVariants) {
            setLoadingVariants(false);
            return;
        }
        setLoadingVariants(true);
        const response = await Api.getProductVariant({partNumber: partNumber});
        if (!isMounted())
            return;
        setLoadingVariants(false);
        if (response?.resultFlag) {
            setVariants(response?.data?.variantParts?.map(e => ({
                ...e,
                coverImageFileName: response?.configuration.PartImageURL?.concat(e?.coverImageFileName ?? '') ?? ''
            })) ?? []);
            setProperties(response?.data?.properties ?? []);
        } else if (response?.code === ApiResponseCodes.productHasNoVariants) {
            setVariants([]);
            setProperties([]);
        }
    }, [isMounted, partNumber, operatorAccessLevels.packages.lookup.productVariants])

    /**
     * With each change in id of the url params:
     * - fetches the technical info properties of the product from the server.
     */
    useEffect(() => {
        void getProductVariants();
    }, [getProductVariants])

    /**
     * Fetches the warehouses of the operator.
     * @type {(function(): Promise<void>)|*}
     */
    const getWarehouses = useCallback(async () => {
        const response = await Api.getOperatorWarehouses();
        if (!isMounted())
            return;
        setWarehouseIds(response?.data?.map(e => e.id) ?? []);
    }, [isMounted])

    /**
     * Fetches the warehouses on component mount.
     */
    useEffect(() => {
        void getWarehouses();
    }, [getWarehouses]);

    const partNosWithNonFetchedAvailabilities = useMemo(() => {
        // Filter or slice based on availabilities
        return variants.map(e => e.partNo).filter(e => Boolean(e) && !availabilities[e]).slice(0, 20);
    }, [variants, availabilities]);

    /**
     * Fetches the availabilities of the products
     * @type {(function(*, *=): Promise<void>)}
     */
    const getProductAvailabilities = useCallback(async (productNos) => {
        const chunkSize = 100;
        setFetchingProductAvailabilities(true);
        const batches = productNos.reduce((arr, productNos, index) => {
            if ((index % chunkSize) === 0) {
                arr.push([]);
            }
            arr[arr.length - 1].push(productNos);
            return arr;
        }, []);
        for (const batch of batches) {
            const response = await Api.getProductAvailabilitiesV2({
                partNumbers: batch,
                warehouseIds: warehouseIds,
            })
            if (!isMounted())
                return
            if (response?.resultFlag) {
                setAvailabilities(prevState => ({
                    ...prevState,
                    ...productNos.reduce((acc, partNo) => {
                        const responseData = response.data.find(r => r.partNumber === partNo);
                        const result = warehouseIds.map(warehouseId => {
                            const localWarehouse = responseData?.localWarehouses?.find(e => e.warehouseId === warehouseId);
                            return ({
                                _has: !!responseData,
                                warehouse: localWarehouse?.warehouseCode,
                                fullName: `${localWarehouse?.warehouseName} ${localWarehouse?.warehouseCode ? `(${localWarehouse?.warehouseCode})` : ''}`,
                                availability: localWarehouse?.availableQty,
                            })
                        });
                        return ({
                            ...acc,
                            [partNo]: result,
                        })
                    }, {}),
                }))
            }
        }
        if (!isMounted())
            return;
        setFetchingProductAvailabilities(false);
    }, [isMounted, warehouseIds])

    /**
     * Fetches the availabilities of the main products in the search result.
     */
    useEffect(() => {
        if (!partNosWithNonFetchedAvailabilities.length || !warehouseIds?.length || fetchingProductAvailabilities || !operatorAccessLevels.packages.bizInventory.availabilityInLookup || loadingViewMode || (viewMode === 'partNo' && !loadingVariants))
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getProductAvailabilities(partNosWithNonFetchedAvailabilities, cancelTokenSource.token);
        return () => cancelTokenSource.cancel();
    }, [partNosWithNonFetchedAvailabilities, getProductAvailabilities, warehouseIds?.length, fetchingProductAvailabilities, operatorAccessLevels.packages.bizInventory.availabilityInLookup, loadingViewMode, viewMode, loadingVariants])

    /**
     * Fetches the key information of the given part numbers.
     * @type {function(*): Promise<*>}
     */
    const getPartKeyInformationFromPartNumbers = useCallback(async (partNumbers) => {
        const promises = partNumbers.map(partNo => Api.getProductKeyInformation(partNo));
        const results = await Promise.all(promises);
        return results.filter(e => e?.resultFlag).map(e => e.data).map(e => ({
            partNo: e.partNo,
            id: e.id,
        }));
    }, [])

    /**
     * Adds the given part numbers to the variants.
     * @type {(function(*): Promise<void>)}
     */
    const addPartNumbersToVariants = useCallback(async (partNumbers) => {
        if (!partNumbers?.length)
            return;
        setLoadingVariants(true);
        const partInformation = await getPartKeyInformationFromPartNumbers(partNumbers);
        if (!isMounted())
            return;
        if (!partInformation?.length) {
            setLoadingVariants(false);
            return;
        }
        const variantPartIds = partInformation.map(e => e.id);
        const response = await Api.makeVariantPart({
            sourcePartId: Number(params.id),
            variantPartsIds: variantPartIds,
        })
        if (!isMounted())
            return;
        setLoadingVariants(false);
        if (response?.resultFlag) {
            // Refresh the variants list
            void getProductVariants();
            void Api.markPartsForCache({partIds: [Number(params.id), ...variantPartIds], sectionNames: [MarkProductForCacheSectionNames.variants]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
    }, [getPartKeyInformationFromPartNumbers, isMounted, params.id, setIsCacheNeeded, getProductVariants])

    /**
     * Removes the given variant from the list.
     * @type {(function(*): Promise<void>)}
     */
    const removeVariant = useCallback(async (product) => {
        if (!product?.partNo)
            return;
        const partIds = [product.id];
        const partNumbers = [product.partNo];
        if (variants?.length === 2) {
            // if removing this variant leaves the product with only itself, we need to remove the variant relationship entirely
            partIds.push(Number(params.id));
            partNumbers.push(partNumber);
        }
        setRemovingPartNumbers(prev => ([...prev, ...partNumbers]));
        const response = await Api.removeVariant({partIds: partIds});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setVariants(prev => prev.filter(e => !partNumbers.includes(e.partNo)));
            Api.markPartsForCache({partIds: partIds, sectionNames: [MarkProductForCacheSectionNames.variants]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
        setRemovingPartNumbers(prev => prev.filter(e => !partNumbers.includes(e)));
    }, [isMounted, params.id, partNumber, setIsCacheNeeded, variants?.length])

    /**
     * Gets the confirmation for removing the planning session.
     */
    const getRemoveConfirmation = useCallback(async (product) => {
        const confirmed = await new Promise((resolve) => {
            dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.confirmation, {
                open: true,
                title: 'Confirm Removal',
                description: `Are you sure you want to remove the option with part number "${product.partNo}" from the options list? This action cannot be undone.`,
                cancelButtonText: "Cancel",
                confirmButtonText: "Continue",
                maxWidth: 'sm',
                fullWidth: false,
                onClose: (confirmed) => {
                    resolve(confirmed);
                    return Promise.resolve(true);
                }
            }));
        })
        if (!isMounted())
            return;
        if (confirmed) {
            removeVariant(product);
        }
    }, [dispatch, removeVariant, isMounted]);

    /**
     * Opens the dialog for Importing the data.
     */
    const openImportDialog = useCallback(() => {
        setImportDialogState({
            open: true,
            close: () => setImportDialogState(initialImportDialogState),
            onImport: async (data) => {
                if (!data?.length)
                    return;
                // Ensure that the current part number is not included and that the part numbers are unique.
                const newPartNumbers = new Set(data.map(e => e.partNo).filter(e => e && e !== partNumber));
                void addPartNumbersToVariants(Array.from(newPartNumbers));
            },
        });
    }, [addPartNumbersToVariants, partNumber]);

    const colors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])
    const augmentedVariants = useMemo(() => {
        return variants.map(product => ({
            ...product,
            removable: product.partNo !== partNumber,
            removing: removingPartNumbers.includes(product.partNo),
            onRemove: () => getRemoveConfirmation(product),
            availabilityInfo: availabilities[product.partNo] ?? [],
            additionalProperties: (() => {
                const seenIds = new Set();
                const merged = [];
                for (const entry of product.properties ?? []) {
                    if (seenIds.has(entry.industryPropertyId)) {
                        continue;
                    }
                    const property = properties.find(p => p.id === entry.industryPropertyId);
                    if (!property) {
                        continue;
                    }
                    seenIds.add(entry.industryPropertyId);
                    const {raw, formatted} = Utils.resolveVariantPropertyDisplay(
                        property.industryPropertyTypeId,
                        product.properties,
                        property.id,
                        property.nullReplacement,
                    );
                    merged.push({
                        id: property.id,
                        title: property.title,
                        value: raw,
                        displayValue: formatted,
                        ...(property.industryPropertyTypeId === ProductTechnicalPropertyTypeIds.color ? {tooltipValue: raw} : {}),
                    });
                }
                return merged;
            })(),
        }))
    }, [variants, availabilities, partNumber, removingPartNumbers, properties, getRemoveConfirmation])

    const applicableProperties = useMemo(() => {
        return properties.filter(property => {
            const values = augmentedVariants.map(product => {
                const {raw, formatted} = Utils.resolveVariantPropertyDisplay(
                    property.industryPropertyTypeId,
                    product.properties,
                    property.id,
                    property.nullReplacement ?? NULL_VALUE_PLACEHOLDER,
                );
                let value = property.industryPropertyTypeId === ProductTechnicalPropertyTypeIds.color
                    ? raw
                    : formatted;
                if (value === null || value === undefined || value === "") {
                    return NULL_VALUE_PLACEHOLDER;
                }
                return value;
            });
            // Include properties even when all variants share one value, so the full variance path is visible
            return values.some(value => value !== NULL_VALUE_PLACEHOLDER);
        });
    }, [properties, augmentedVariants]);

    const hierarchies = useMemo(() => {
        const obj = createHierarchyObject(augmentedVariants, applicableProperties);
        return hierarchyObjectToArray(obj, applicableProperties, colors);
    }, [augmentedVariants, applicableProperties, colors])

    const existingPartNumbers = React.useMemo(() => Array.from(new Set([...variants.map(e => e.partNo), partNumber])), [partNumber, variants]);

    return (
        <>
            <Box className={'w-100 part-info-paper pb-4'}>
                <div className={'d-flex justify-content-between align-items-center mb-5 mx-3 mt-3'}>
                    {
                        !!applicableProperties.length &&
                        <Box sx={{display: 'flex', flexDirection: 'row', gap: 0.5, flexWrap: 'wrap', alignItems: 'center'}}>
                            <Typography variant={'body1'} fontWeight={600} sx={{width: '100%'}} gutterBottom>
                                Current Options Hierarchy Levels:
                            </Typography>
                            {
                                applicableProperties.map((property, i, array) => (
                                    <React.Fragment key={property.id}>
                                        <Chip
                                            label={property.title}
                                            variant={'filled'}
                                            color={'primary'}
                                            size={'medium'}
                                            sx={{
                                                fontWeight: 600,
                                            }}
                                        />
                                        {
                                            i < array.length - 1 &&
                                            <ArrowRightAltOutlined/>
                                        }
                                    </React.Fragment>
                                ))
                            }
                        </Box>
                    }
                    <Button
                        disabled={loadingVariants}
                        variant={'outlined'}
                        color={'primary'}
                        sx={{paddingBlock: 0.5, paddingInline: 1.5}}
                        onClick={openImportDialog}
                        startIcon={<Excel/>}
                    >
                        Import Data
                    </Button>
                </div>
                <Grid container spacing={5} sx={{paddingInline: 2}}>
                    {
                        loading &&
                        <Grid xs={12}>
                            <LoadingLottieAnimation className={''} width={'80%'}/>
                        </Grid>
                    }
                    {
                        !loading &&
                        <Grid xs={12}>
                            <AddVariancesAutocomplete
                                industryId={product?.industryId}
                                existingPartNumbers={existingPartNumbers}
                                onAddPartNumbers={addPartNumbersToVariants}
                                disabled={loadingVariants}
                            />
                        </Grid>
                    }
                    {
                        !loading && variants.length === 0 &&
                        <Grid xs={12} className={'p-4 d-flex align-items-center justify-content-center my-5'}>
                            <Typography variant={'body2'} textAlign={'center'}>
                                This product currently has no options. Click on the "Import Data" button or use the above Autocomplete to add your desired options.
                            </Typography>
                        </Grid>
                    }
                    {
                        !loading && variants.length > 0 &&
                        <>
                            <Grid xs={12}>
                                <VarianceTable
                                    variants={augmentedVariants}
                                />
                            </Grid>
                            <Grid xs={12}>
                                <HierarchyTreeView
                                    hierarchies={hierarchies}
                                    currentPartNumber={partNumber}
                                    loadingViewMode={loadingViewMode}
                                    viewMode={viewMode}
                                    toggleViewMode={toggleViewMode}
                                />
                            </Grid>
                        </>
                    }
                </Grid>
            </Box>
            <ProductVarianceImportDialog {...importDialogState} />
        </>
    );
}


const AddVariancesAutocomplete = ({existingPartNumbers, onAddPartNumbers, industryId, disabled}) => {
    const [newParts, setNewParts] = useState([]);

    /**
     * Removes part numbers that are already existing from the newPartNumbers state.
     */
    useEffect(() => {
        setNewParts(prevState => prevState.filter(e => !existingPartNumbers.includes(e.partNo)));
    }, [existingPartNumbers])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <PartAutocomplete
                    disableKeywordReset
                    shouldRemoveOptionsOnEmptyKeywords={false}
                    industryId={industryId}
                    multiple
                    disableCloseOnSelect
                    disabled={disabled}
                    filterOptions={(options) => options.filter(e => !existingPartNumbers.includes(e.partNo))}
                    label={'Add Options'}
                    placeholder={'Search and add part numbers to the options list...'}
                    renderTags={() => ""}
                    value={newParts}
                    renderOption={(props, option) => {
                        const selected = newParts.some(e => e.partNo === option.partNo);
                        return (
                            <li {...props} key={option.partNo}>
                                <Checkbox checked={selected} sx={{marginRight: 1}}/>
                                {option.partNo}
                            </li>
                        );
                    }}
                    onChange={(_, parts) => setNewParts(parts)}
                />
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start', flexWrap: 'wrap'}}>
                    {
                        newParts.map(part => (
                            <Chips
                                key={part.partNo}
                                disabled={disabled}
                                contentType={ChipsContentType.element}
                                iconPosition={ChipsIconPosition.end}
                                type={ChipsTypes.remove}
                                onIconClick={() => setNewParts(prevState => prevState.filter(e => e.partNo !== part.partNo))}
                                content={
                                    <div className={'chips-content'}>
                                        <ProductInformationPopover partNo={part.partNo ?? ''}>
                                            {part.partNo ?? ''}
                                        </ProductInformationPopover>
                                    </div>
                                }
                            />
                        ))
                    }
                    {
                        !!newParts.length &&
                        <Button
                            sx={{alignSelf: 'center', justifySelf: 'flex-end', marginInlineStart: 'auto'}}
                            variant={'contained'} size={'small'} color={'primary'} disabled={disabled}
                            onClick={() => onAddPartNumbers(newParts.map(e => e.partNo))}
                        >
                            Add {newParts.length} Part Number{newParts.length > 1 ? 's' : ''}
                        </Button>
                    }
                </Box>
            </Box>
        </>
    );

}


const dataGridColumnsNames = {
    partNo1: 'partNo1',
    partNo2: 'partNo2',
    partNo3: 'partNo3',
}

const dataGridSaveConfig = {
    name: 'ProductVarianceImportWithPrefixDG',
    version: 1,
}

const VarianceTable = ({variants}) => {

    /**@type {DataGridColumn[]}*/
    const columns = useMemo(() => [
        {
            name: dataGridColumnsNames.partNo1,
            title: 'Part No',
            type: DataGridColumnTypes.string,
            width: {size: 1, type: DataGridColumnWidthTypes.flex},
            sortable: false,
        },
        {
            name: dataGridColumnsNames.partNo2,
            title: 'Part No',
            type: DataGridColumnTypes.string,
            width: {size: 1, type: DataGridColumnWidthTypes.flex},
            sortable: false,
        },
        {
            name: dataGridColumnsNames.partNo3,
            title: 'Part No',
            type: DataGridColumnTypes.string,
            width: {size: 1, type: DataGridColumnWidthTypes.flex},
            sortable: false,
        },
    ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    const dataGridRows = useMemo(() => {
        const resultsInThree = variants.reduce((resultArray, item, index) => {
            const chunkIndex = Math.floor(index / 3);
            if (!resultArray[chunkIndex]) {
                resultArray[chunkIndex] = []; // start a new chunk
            }

            resultArray[chunkIndex].push(item);
            return resultArray;
        }, []);

        const generateCell = (product) => {
            if (!product)
                return " ";
            return (
                <Tooltip title={product && !product.removable ? "The current product" : ""} placement={'top'}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        {
                            product.removable &&
                            <IconButton
                                onPressed={product.onRemove}
                                disabled={product.removing}
                                color={'paper.error'}
                            >
                                {
                                    product.removing
                                        ? <CircularProgress size={20} color={'secondary'}/>
                                        : <RemoveIcon className='icon large'/>
                                }
                            </IconButton>
                        }
                        <ProductInformationPopover partNo={product.partNo}>
                            {product.partNo ?? ''}
                        </ProductInformationPopover>
                    </Box>
                </Tooltip>
            )
        }

        return resultsInThree.map(([product1, product2, product3]) => ({
            key: `${product1.partNo}-${product2?.partNo ?? 'nopart'}-${product3?.partNo ?? 'nopart'}`,
            data: [product1, product2, product3],
            cells: {
                [dataGridColumnsNames.partNo1]: generateCell(product1),
                [dataGridColumnsNames.partNo2]: generateCell(product2),
                [dataGridColumnsNames.partNo3]: generateCell(product3),
            },
        }));
    }, [variants]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mb: 4}}>
                <Typography variant={'body1'} fontWeight={600}>
                    Options Table
                </Typography>
                <PartnerDataGrid
                    classNames={{toolbar: "pt-0"}}
                    visibleRows={5}
                    {...dataGridSaveConfig}
                    columns={dataGridColumns}
                    initialLayoutState={{columns: columns}}
                    rows={dataGridRows}
                    useVirtualizedRows
                    state={{
                        pagination: {pageSize: variants.length, currentPage: 1, length: variants.length},
                        loading: {state: false},
                        density: dataGridDensity,
                    }}
                    hideFooter
                    hideExporting
                />
            </Box>
        </>
    );
}


const HierarchyTreeView = ({
                               hierarchies,
                               currentPartNumber,
                               loadingViewMode,
                               viewMode,
                               toggleViewMode,
                           }) => {
    const [collapsed, setCollapsed] = useState({});
    const theme = useTheme();

    /**
     * As soon as the component mounts, scrolls to the head of the tree.
     */
    useEffect(() => {
        const headOfTree = document.getElementById('head-of-tree');
        if (!headOfTree) return;
        headOfTree.scrollIntoView({behavior: 'instant', block: 'center', inline: 'center'});
    }, []);

    /**
     * Scrolls to the current product in the tree.
     * @type {(function(): void)|*}
     */
    const scrollToCurrentProduct = useCallback(() => {
        const element = document.getElementById(currentPartNumber);
        if (!element) return;
        element.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'});
    }, [currentPartNumber])

    /**
     * Collapses all the nodes of the tree.
     * @type {(function(): void)}
     */
    const collapseAll = useCallback(() => {
        const newCollapsed = {};
        const traverse = (nodes) => {
            for (const node of nodes) {
                newCollapsed[node.id] = true;
                if (node.children)
                    traverse(node.children);
            }
        };
        traverse(hierarchies);
        setCollapsed(newCollapsed);
    }, [hierarchies]);

    /**
     * Expands all the nodes of the tree.
     * @type {(function(): void)}
     */
    const expandAll = useCallback(() => {
        setCollapsed({});
    }, []);

    /**
     * Toggles the collapse state of the given node.
     * @type {(function(*): void)}
     */
    const onCollapse = useCallback((nodeId) => {
        setCollapsed(prev => ({
            ...prev,
            [nodeId]: !prev[nodeId],
        }))
    }, []);

    /**
     * Toggles the expand state of the given node.
     * @type {(function(*): void)}
     */
    const onExpand = useCallback((nodeId) => {
        setCollapsed(prev => ({
            ...prev,
            [nodeId]: !prev[nodeId],
        }))
    }, []);

    const augmentedHierarchies = useMemo(() => {
        const augment = (nodes) => {
            return nodes.map(node => ({
                ...node,
                children: node.children ? augment(node.children) : undefined,
                isCollapsed: !!collapsed[node.id],
                onCollapse: () => onCollapse(node.id),
                onExpand: () => onExpand(node.id),
            }))
        };
        return augment(hierarchies);
    }, [hierarchies, collapsed, onCollapse, onExpand]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2}}>
                    <Typography variant={'body1'} fontWeight={600}>
                        Options Hierarchy Tree
                    </Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1}}>
                        <Button
                            variant={'outlined'}
                            color={'secondary'}
                            className={'d-flex align-items-center'}
                            onClick={() => scrollToCurrentProduct()}
                        >
                            <SearchOutlined/>
                            Highlight Current Product
                        </Button>
                        <Button
                            variant={'outlined'}
                            color={'secondary'}
                            className={'d-flex align-items-center'}
                            onClick={() => expandAll()}
                        >
                            <UnfoldMoreRounded className={'icon small'}/>
                            Expand all levels
                        </Button>
                        <Button
                            variant={'outlined'}
                            color={'secondary'}
                            className={'d-flex align-items-center'}
                            onClick={() => collapseAll()}
                        >
                            <UnfoldLessRounded className={'icon small'}/>
                            Collapse all levels
                        </Button>
                        <Stack sx={{border: "1px solid transparent", borderColor: 'divider', padding: 0.5, borderRadius: 1, width: 'fit-content',}}>
                            <ToggleButtonGroup
                                disabled={loadingViewMode}
                                sx={{
                                    '& .MuiToggleButtonGroup-grouped': {
                                        borderRadius: "0.5rem",
                                        width: 30,
                                        height: 30,
                                    },
                                    "& .MuiToggleButtonGroup-grouped:not(:first-of-type)": {
                                        marginLeft: ".25rem",
                                    },
                                }}
                                size={'small'}
                                value={viewMode}
                                onChange={(_, v) => toggleViewMode(v)}
                                exclusive
                            >
                                <ToggleButton
                                    value={"list"}
                                    sx={{backgroundColor: viewMode === 'card' ? "divider" : "transparent", border: "none"}}>
                                    <Tooltip title={'Show product card'}>
                                        <WidthFullRounded sx={{padding: 0.25}}/>
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton
                                    value={"grid"}
                                    sx={{backgroundColor: viewMode === 'partNo' ? "divider" : "transparent", border: "none"}}>
                                    <Tooltip title={"Show part number only"}>
                                        <TextFormatRounded sx={{padding: 0.25}}/>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Box>
                </Box>
                <Box sx={{maxWidth: '100%', overflowX: 'auto', minHeight: '50vh'}}>
                    <Box component={Tree}
                         label={
                             <Card
                                 variant={'outlined'}
                                 sx={{
                                     justifySelf: 'center',
                                     alignSelf: 'center',
                                     padding: 1,
                                     borderRadius: 1,
                                     display: 'flex',
                                     alignItems: 'center',
                                     width: 'fit-content',
                                     backgroundColor: (theme) => `rgba(${theme.palette.tertiary.mainChannel} / ${theme.palette.action.focusOpacity})`,
                                     borderColor: 'tertiary.main',
                                 }}
                             >
                                 <Typography color={'tertiary.main'} variant={'body1'} fontWeight={600} id={'head-of-tree'}>
                                     Product Options
                                 </Typography>
                             </Card>
                         }
                         lineBorderRadius={'8px'}
                         lineColor={theme.palette.divider}
                         lineStyle={'dashed'}
                         lineWidth={'2px'}
                         lineHeight={'30px'}
                    >
                        {
                            augmentedHierarchies.map(node => (
                                <CustomNode
                                    key={node.id}
                                    node={node}
                                    viewMode={viewMode}
                                />
                            ))
                        }
                    </Box>
                </Box>
            </Box>
        </>
    );
}

const CustomNode = ({node, viewMode}) => {

    return (
        <>
            <Box component={TreeNode} sx={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center'}}
                 label={
                     <Stack gap={1}>
                         {
                             node.showProducts &&
                             node.products.map(product => (
                                 <Tooltip key={product.id} title={!product.removable ? "The current product" : ""} placement={'top'}>
                                     <Box
                                         component={'span'}
                                         sx={{
                                             display: 'flex',
                                             justifyContent: 'center',
                                             alignItems: 'flex-start',
                                             width: viewMode === 'partNo' ? 160 : 260,
                                             position: 'relative',
                                             '.lookup-product-card': {
                                                 boxShadow: 'none',
                                                 ...(!product.removable && {
                                                     borderColor: 'primary.main',
                                                 })
                                             },
                                             '.product-card-part-number': {
                                                 ...(!product.removable && {
                                                     borderColor: 'primary.main',
                                                 })
                                             }
                                         }}
                                         id={product.partNo}
                                     >
                                         {
                                             viewMode === 'card' &&
                                             <>
                                                 {
                                                     product.removable &&
                                                     <Box component={'span'} sx={{position: 'absolute', right: -8, top: -8, zIndex: 1, transform: '-50%, -50%'}}>
                                                         <Tooltip arrow title={'Remove product as a variant'}>
                                                             <IconButton
                                                                 onPressed={product.onRemove}
                                                                 disabled={product.removing}
                                                                 color={'paper.error'}
                                                                 sx={{
                                                                     borderColor: 'error.main',
                                                                     border: '1px solid',
                                                                     "&:hover": {
                                                                         backgroundColor: 'error.main',
                                                                         color: 'background.paper',
                                                                         borderColor: 'error.main',
                                                                     },
                                                                 }}
                                                             >
                                                                 {
                                                                     product.removing
                                                                         ? <CircularProgress size={20} color={'secondary'}/>
                                                                         : <RemoveIcon className='icon large'/>
                                                                 }
                                                             </IconButton>
                                                         </Tooltip>
                                                     </Box>
                                                 }
                                                 <LookupProductCard
                                                     info={{
                                                         partNo: product.partNo,
                                                         images: [product.coverImageFileName]
                                                     }}
                                                     readonly
                                                     showTooltip={false}
                                                     availabilityInfo={product.availabilityInfo}
                                                     additionalProperties={product.additionalProperties}
                                                 />
                                             </>
                                         }
                                         {
                                             viewMode === 'partNo' &&
                                             <Card
                                                 variant={'outlined'}
                                                 className={'product-card-part-number'}
                                                 sx={{
                                                     padding: 1,
                                                     borderRadius: 1,
                                                     display: 'flex',
                                                     alignItems: 'center',
                                                     flexShrink: 0,
                                                     width: '100%',
                                                     position: 'relative',
                                                     overflow: 'visible'
                                                 }}
                                             >
                                                 {
                                                     product.removable &&
                                                     <Box component={'span'} sx={{position: 'absolute', right: -16, top: -16, zIndex: 1, transform: '-50%, -50%'}}>
                                                         <Tooltip arrow title={'Remove product as a variant'}>
                                                             <IconButton
                                                                 onPressed={product.onRemove}
                                                                 disabled={product.removing}
                                                                 color={'paper.error'}
                                                                 sx={{
                                                                     borderColor: 'error.main',
                                                                     border: '1px solid',
                                                                     "&:hover": {
                                                                         backgroundColor: 'error.main',
                                                                         color: 'background.paper',
                                                                         borderColor: 'error.main',
                                                                     },
                                                                 }}
                                                             >
                                                                 {
                                                                     product.removing
                                                                         ? <CircularProgress size={20} color={'secondary'}/>
                                                                         : <RemoveIcon className='icon large'/>
                                                                 }
                                                             </IconButton>
                                                         </Tooltip>
                                                     </Box>
                                                 }
                                                 <ProductInformationPopover partNo={product.partNo}>
                                                     {product.partNo ?? ""}
                                                 </ProductInformationPopover>
                                             </Card>
                                         }
                                     </Box>
                                 </Tooltip>
                             ))
                         }
                         {
                             !node.showProducts &&
                             <Card
                                 variant={'outlined'}
                                 sx={{
                                     padding: 1,
                                     borderRadius: 1,
                                     display: 'flex',
                                     alignItems: 'center',
                                     flexShrink: 0,
                                     width: 'max-content',
                                     backgroundColor: node.backgroundColor,
                                     borderColor: node.color,
                                     gap: 0.5
                                 }}
                             >
                                 <Typography color={node.color} variant={'body2'} fontWeight={600} sx={{flexShrink: 0}}>
                                     {node.property?.title ?? ""}: {node.value}
                                 </Typography>
                                 {
                                     node.children?.length > 0 &&
                                     <IconButton
                                         size={'small'}
                                         sx={{p: 0}}
                                         onClick={() => node.isCollapsed ? node.onExpand() : node.onCollapse()}
                                         color={'paper.primary'}
                                     >
                                         {node.isCollapsed ? <ExpandMoreRounded/> : <ExpandLessRounded/>}
                                     </IconButton>
                                 }
                             </Card>
                         }
                     </Stack>
                 }
            >
                {
                    !node.isCollapsed
                        ? node.children?.map((childNode) => (
                            <CustomNode
                                key={childNode.id}
                                node={childNode}
                                viewMode={viewMode}
                            />
                        ))
                        : undefined
                }
            </Box>
        </>
    );
};


export default ProductVarianceSection;
